import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useDebounce } from 'usehooks-ts';

import { InputAutoComplete } from '@/ui/index';
import { hooks as viewHooks } from '@/domains/view';
import { useBusiness } from '@/hooks/useBusiness';
import { Search } from '@/icons/index';
import useOnClickOutside from '@/hooks/useClickOutside';
import { jsEscape } from '@/utils/encodeStr';

import styles from './SearchField.module.scss';

const SearchField = ({ active, setActive, setOpenMenu }) => {
  const { t, lang } = useTranslation();
  const router = useRouter();
  const isBusiness = useBusiness();

  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const [value, setValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const debouncedValue = useDebounce(value, 800);

  const { data, isFetching } = viewHooks.useSearchContent(
    {
      q: debouncedValue,
      type: isBusiness ? 'business' : 'individual',
      lang,
    },
    {
      skip: !debouncedValue,
    },
  );
  const handleInputChange = (newValue) => {
    if (newValue) {
      setIsTyping(true);
    }
    setValue(newValue);
  };

  useEffect(() => {
    if (active) inputRef.current?.focus();
  }, [active]);

  const handleClose = () => {
    setValue('');
  };

  useOnClickOutside(containerRef, () => setActive(false));

  const handleSearchRedirection = (item) => {
    setOpenMenu();
    router.push(item?.slug);
    handleClose();
    setActive(!active);
  };

  const endIconClick = () => {
    setActive(false);
    router.push(
      `/search?q=${jsEscape(value)}&type=${
        isBusiness ? 'corporate' : 'individual'
      }`,
    );
  };

  useEffect(() => {
    const inputElement = inputRef.current;

    const handleKeyPress = (event) => {
      if (value && event.key === 'Enter') {
        if (value.trim() !== '') {
          endIconClick();
          setOpenMenu();
          handleClose();
          setActive(!active);
        }
      }
    };

    if (inputElement) {
      inputElement.addEventListener('keypress', handleKeyPress);
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener('keypress', handleKeyPress);
      }
    };
  }, [debouncedValue, value]);

  useEffect(() => {
    setIsTyping(false);
  }, [debouncedValue]);

  return (
    <div
      className={classNames([styles.wrapper, { [styles.active]: active }])}
      itemScope
      itemType='https://schema.org/WebPage'
    >
      <div
        ref={containerRef}
        className={classNames(['wrapper', styles.content])}
        itemScope
        itemType='https://schema.org/SearchAction'
      >
        <InputAutoComplete
          inputRef={inputRef}
          className={styles.input}
          value={value}
          setValue={handleInputChange}
          searchList={data}
          placeholder={t('common:Search')}
          onSelect={handleSearchRedirection}
          endIcon={<Search className={styles.red_icon} />}
          {...(value && { endIconClick })}
          loading={isFetching}
          isTyping={isTyping}
        />
      </div>
    </div>
  );
};

export default SearchField;
